import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./client_panel.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";
import pureVegIcon from "../../assets/pure-veg-symbol.jpg";
import nonVegIcon from "../../assets/non-veg-symbol.jpg";
import parse from "html-react-parser";

interface User {
  _id: string;
  name: string;
  phoneNumber: string;
  email: string;
  gender: string;
  age: string;
  weight: string;
  height: string;
  goal: string;
  activity: string;
  medicalConcern: string;
  conditions: { [key: string]: boolean | string }; // More specific type for conditions
  startingBmi: string;
  languageCode: string;
  profilePicture: string;
  activePlans: any[]; // Assuming an array of objects
  historyPlans: any[]; // Assuming an array of objects
  weightEntries: object;
  dietPlan: { mealType: string; diet: string }[]; // More specific type for dietPlan
  createdAt: Date;
  updatedAt: Date;
  totalCalorieSearchApiCalls: number;
  activeCalorieTrackerBySearchPlan: any;
  historyCalorieTrackerBySearchPlans: any;
  dietaryType: string;
  lastOnline: Date;
  registrationDevice: string;
  goalWeightLoss: string;
  goalWeightGain: string;
  goalMuscleGain: string;
  goalHealthIssues: string;
  appliedCoupon: string;
  assignedTo: string | null;
  clientStatus?: string;
  comments?: string;
}

const ClientPanel = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedUsers, setExpandedUsers] = useState<{
    [key: string]: boolean;
  }>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const [salesPersonName, setSalesPersonName] = useState<string>(""); // Renamed for clarity, was initially just 'salesPersonName'
  const [showDietPlanModal, setShowDietPlanModal] = useState<boolean>(false);
  const [currentDietPlan, setCurrentDietPlan] = useState<
    { mealType: string; diet: string }[]
  >([]);
  const [selectedMealType, setSelectedMealType] = useState<string | null>(
    "General Instructions"
  );
  const [showClientStatusModal, setShowClientStatusModal] =
    useState<boolean>(false);
  const [showClientsResponseModal, setShowClientsResponseModal] =
    useState<boolean>(false);
  const [modalInputValue, setModalInputValue] = useState<string>(""); // Used for both status and comments
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [selectedUserPhone, setSelectedUserPhone] = useState<string>("");
  const [selectedClientStatusFilter, setSelectedClientStatusFilter] =
    useState<string>("");

  const clientStatusOptions = [
    "Interested/Follow up",
    "Discussed",
    "Not Interested",
    "Call Not Connected",
    "Call Not Received",
    "Call Received but Call Cut",
    "Blacklist",
    "Callback",
    "Junk Lead",
    "Subscribed",
    "Internal Team Member",
    "Applied by Mistake",
    "Invalid",
    "Interested in low budget",
    "Interested for 1 month"
  ];

  const languageMap = {
    en: "English",
    hi: "हिन्दी (Hindi)",
    bn: "বাংলা (Bangla)",
    mr: "मराठी (Marathi)",
    pa: "ਪੰਜਾਬੀ (Punjabi)",
    te: "తెలుగు (Telugu)",
    ta: "தமிழ் (Tamil)",
    gu: "ગુજરાતી (Gujrati)",
    ur: "اردو (Urdu)",
    kn: "ಕನ್ನಡ (Kannada)",
    or: "ଓଡିଆ (Odia)",
    ml: "മലയാളം (Malayalam)",
    as: "অসমীয়া (Assamese)",
  };

  // Fetch user details and users
  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-sales-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      setSalesPersonName(response.data.name);
      fetchAllUsers();
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Session expired or invalid token! Redirecting to login.");
      handleLogout();
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-users-info`
      );
      setUsers(response.data.data.reverse());
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  // Expand or collapse individual user cards
  const toggleExpanded = (userId: string) => {
    setExpandedUsers((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const timeAgo = (dateString: string): string => {
    const now = new Date().getTime();
    const pastDate = new Date(dateString).getTime();
    const diffInSeconds = Math.floor((now - pastDate) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} sec ago`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)} min ago`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)} h ago`;
    } else if (diffInSeconds < 2592000) {
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    } else if (diffInSeconds < 31536000) {
      return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    } else {
      return `${Math.floor(diffInSeconds / 31536000)} years ago`;
    }
  };

  const convertHeightToMeters = (heightString: string): number => {
    if (!heightString) return 0;

    if (heightString.includes("ft")) {
      const feetPattern = /(\d+)\s*ft/;
      const inchesPattern = /(\d+)\s*in/;

      const feetMatch = heightString.match(feetPattern);
      const inchesMatch = heightString.match(inchesPattern);

      const feet = feetMatch ? parseFloat(feetMatch[1]) : 0.0;
      const inches = inchesMatch ? parseFloat(inchesMatch[1]) : 0.0;

      const totalInches = feet * 12 + inches;
      return totalInches * 0.0254; // Convert inches to meters
    } else if (heightString.includes("cm")) {
      const valuePart = heightString.replace(/[^\d.]/g, "");
      const heightInCm = parseFloat(valuePart) || 0.0;
      return heightInCm / 100; // Convert cm to meters
    }
    return 0;
  };

  const calculateMinNormalWeightRange = (
    bmi: number,
    heightInMeter: number
  ) => {
    const result = bmi * heightInMeter * heightInMeter;
    return result;
  };

  const openAssignSalesPersonModal = (phoneNumber: string) => {
    setSelectedUserPhone(phoneNumber);
    const currentUser = users.find((user) => user.phoneNumber === phoneNumber);
    setModalInputValue(currentUser?.clientStatus || "");
    setShowClientStatusModal(true);
  };

  const openAssignSalesPersonResponseModal = (
    phoneNumber: string,
    comments: string | undefined
  ) => {
    setSelectedUserPhone(phoneNumber);
    setModalInputValue(comments || "");
    setShowClientsResponseModal(true);
  };

  const handleUpdateClientStatus = async () => {
    if (!modalInputValue) {
      alert("Please select a status");
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(
        "https://api.fitoja.in/api/edit-personal-info",
        {
          phoneNumber: selectedUserPhone,
          clientStatus: modalInputValue,
        },
        config
      );

      setUsers(
        users.map((user) =>
          user.phoneNumber === selectedUserPhone
            ? { ...user, clientStatus: modalInputValue }
            : user
        )
      );

      setShowClientStatusModal(false);
    } catch (error) {
      console.error("Failed to update client status:", error);
      alert("Failed to update client status. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateClientComments = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(
        "https://api.fitoja.in/api/edit-personal-info",
        {
          phoneNumber: selectedUserPhone,
          comments: modalInputValue,
        },
        config
      );

      setUsers(
        users.map((user) =>
          user.phoneNumber === selectedUserPhone
            ? { ...user, comments: modalInputValue }
            : user
        )
      );

      setShowClientsResponseModal(false);
    } catch (error) {
      console.error("Failed to save response:", error);
      alert("Failed to save response. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  const filteredUsers = users.filter((user) => {
    const matchesAssignedTo = user.assignedTo === salesPersonName;
    const matchesSearchTerm = user.phoneNumber.includes(searchTerm);
    const matchesClientStatus =
      selectedClientStatusFilter === "" ||
      user.clientStatus === selectedClientStatusFilter;

    return matchesAssignedTo && matchesSearchTerm && matchesClientStatus;
  });

  const getDiseaseDisplayName = (
    key: string,
    conditions: { [key: string]: boolean | string }
  ) => {
    const diseaseNames: { [key: string]: string } = {
      diabetes: "Diabetes",
      menopause: "Menopause",
      pcosPcod: "PCOS/PCOD",
      cardiovascular: "Cardiovascular",
      immunity: "Immunity",
      hairAndSkin: "Hair & Skin",
      highUricAcid: "High Uric Acid",
      anemia: "Anemia",
      ulcerativeColitis: "Ulcerative Colitis",
      crohnsDisease: "Crohn's Disease",
      sle: "SLE",
      psoriasis: "Psoriasis",
      hashimotos: "Hashimoto's",
      irritableBowelSyndrome: "Irritable Bowel Syndrome",
      thyroidism: "Thyroidism",
      antiCancerDiets: "Anti-Cancer Diets",
    };
    if (
      key === "otherMedicalConditions" &&
      conditions["otherMedicalConditionsDes"]
    ) {
      return conditions["otherMedicalConditionsDes"] as string;
    }
    return diseaseNames[key] || null;
  };

  return (
    <div className="client-panel-container">
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading clients, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          {/* <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} alt="Fitoja Logo" />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header> */}

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <button
                  className="close-button"
                  onClick={() => setShowOverlay(false)}
                >
                  <img src={closeIcon} alt="Close" />
                </button>
                <h3>My Profile</h3>
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          {/* Client's Status Modal */}
          {showClientStatusModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Update Client Status</h3>
                <select
                  value={modalInputValue}
                  onChange={(e) => setModalInputValue(e.target.value)}
                  className="status-select"
                >
                  <option value="">Select Status</option>
                  {clientStatusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="modal-actions">
                  <button
                    onClick={() => setShowClientStatusModal(false)}
                    className="button button-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateClientStatus}
                    className="button button-primary"
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Status"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Client's Response Text Modal */}
          {showClientsResponseModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Edit Client Comments</h3>
                <textarea
                  value={modalInputValue}
                  rows={6}
                  onChange={(e) => setModalInputValue(e.target.value)}
                  placeholder="Enter comments here..."
                ></textarea>
                <div className="modal-actions">
                  <button
                    onClick={() => setShowClientsResponseModal(false)}
                    className="button button-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateClientComments}
                    className="button button-primary"
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Comments"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showDietPlanModal && (
            <div className="modal-overlay">
              <div className="diet-plan-modal">
                <div className="modal-header">
                  <h3>Client's Diet Plan</h3>
                  <button
                    className="close-button"
                    onClick={() => {
                      setShowDietPlanModal(false);
                      setSelectedMealType("General Instructions"); // Reset to default
                    }}
                  >
                    <img src={closeIcon} alt="Close" />
                  </button>
                </div>
                <div className="meal-type-tabs">
                  {currentDietPlan.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedMealType(item.mealType)}
                      className={`meal-type-tab ${
                        selectedMealType === item.mealType ? "active" : ""
                      }`}
                    >
                      {parse(item.mealType)}
                    </button>
                  ))}
                </div>

                <div className="diet-content">
                  {currentDietPlan.map((item, index) =>
                    selectedMealType === item.mealType ? (
                      <div key={index} className="diet-text">
                        {parse(item.diet)}
                      </div>
                    ) : null
                  )}
                </div>
              </div>
            </div>
          )}

          <main className="client-dashboard-main">
            <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
              <div onClick={() => navigate("/dashboard")}>
                <img
                  style={{ height: "50px", cursor: "pointer" }}
                  src={logo}
                  alt="Fitoja Logo"
                />
              </div>
              <h2 className="main-title">Client Report Tracker</h2>
            </div>
            <div className="filters-and-search">
              <input
                type="text"
                placeholder="Search by Phone Number"
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select
                value={selectedClientStatusFilter}
                onChange={(e) => setSelectedClientStatusFilter(e.target.value)}
                className="status-filter-select"
              >
                <option value="">All Client Status</option>
                {clientStatusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div className="user-list">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <div key={user._id} className="user-card">
                    <div
                      className="user-card-header"
                      onClick={() => toggleExpanded(user._id)}
                    >
                      <h4 className="user-name">{user.name}</h4>
                      <span className="user-phone">{user.phoneNumber}</span>
                      <span className="expand-indicator">
                        {expandedUsers[user._id] ? "▲" : "▼"}
                      </span>
                    </div>

                    <div className="user-card-body">
                      <p>
                        <strong>Email:</strong> {user.email}
                      </p>
                      <p>
                        <strong>Gender:</strong> {user.gender}
                      </p>
                      <p>
                        <strong>Age:</strong> {user.age}
                      </p>
                      <p>
                        <strong>Height:</strong> {user.height}
                      </p>
                      <p>
                        <strong>Weight:</strong> {user.weight} kg
                      </p>
                      <p>
                        <strong>BMI (Joining):</strong>{" "}
                        {parseFloat(user.startingBmi).toFixed(2)}
                      </p>
                      <p>
                        <strong>Normal Weight Range:</strong>{" "}
                        {calculateMinNormalWeightRange(
                          18.5,
                          convertHeightToMeters(user.height)
                        ).toFixed(1)}{" "}
                        kg -{" "}
                        {calculateMinNormalWeightRange(
                          24.9,
                          convertHeightToMeters(user.height)
                        ).toFixed(1)}{" "}
                        kg
                      </p>
                      <p className="dietary-type">
                        <strong>Dietary Type:</strong>
                        {user.dietaryType === "Vegetarian" ? (
                          <span className="veg-container">
                            <img src={pureVegIcon} alt="Pure Veg" />
                            <span>Vegetarian</span>
                          </span>
                        ) : (
                          <span className="nonveg-container">
                            <img src={nonVegIcon} alt="Non-Veg" />
                            <span>Non-Vegetarian</span>
                          </span>
                        )}
                      </p>
                      <p className="user-goals">
                        <strong>Goals:</strong>
                        {user.goalWeightLoss === "true" && (
                          <span className="goal-tag">Weight Loss</span>
                        )}
                        {user.goalWeightGain === "true" && (
                          <span className="goal-tag">Weight Gain</span>
                        )}
                        {user.goalMuscleGain === "true" && (
                          <span className="goal-tag">Muscle Gain</span>
                        )}
                        {user.goalHealthIssues === "true" && (
                          <span className="goal-tag">Other Health Issues</span>
                        )}
                      </p>
                      <p>
                        <strong>Activity:</strong> {user.activity}
                      </p>
                      {user.medicalConcern === "Yes" ? (
                        <p className="user-diseases">
                          <strong>Diseases:</strong>
                          <span className="disease-list">
                            {Object.entries(user.conditions)
                              .filter(
                                ([, value]) =>
                                  value === true || typeof value === "string"
                              )
                              .map(([key]) =>
                                getDiseaseDisplayName(key, user.conditions)
                              )
                              .filter(Boolean)
                              .join(" | ")}
                          </span>
                        </p>
                      ) : (
                        <p>
                          <strong>Diseases:</strong>{" "}
                          <span className="no-disease">No disease</span>
                        </p>
                      )}
                      <p>
                        <strong>Language:</strong>{" "}
                        {languageMap[user.languageCode] || user.languageCode}
                      </p>
                      <p>
                        <strong>Registered on:</strong>{" "}
                        <span>
                          {new Date(user.createdAt)
                            .toLocaleString("en-GB", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                              hour: "numeric",
                              minute: "numeric",
                              hour12: true,
                            })
                            .replace(",", "")
                            .replace(/(\d)(?=\d{2}\:)/, "$1 ")}
                        </span>
                      </p>
                      <p>
                        <strong>Subscription Status:</strong>{" "}
                        {user.activePlans.length == 0
                          ? "Not Subscribed"
                          : "Subscribed"}
                      </p>

                      {expandedUsers[user._id] && (
                        <div className="expanded-details">
                          <div className="plan-section">
                            <strong>Active Plans:</strong>
                            {user.activePlans && user.activePlans.length > 0 ? (
                              <div className="plan-list">
                                {user.activePlans.map((plan, index) => (
                                  <div key={index} className="plan-item">
                                    <p>
                                      <strong>
                                        {index + 1}. {plan.planName}:
                                      </strong>
                                    </p>
                                    <ul>
                                      <li>Price: ₹{plan.price}</li>
                                      <li>
                                        Start date:{" "}
                                        {new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.startDate))}
                                      </li>
                                      <li>
                                        End date:{" "}
                                        {new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.endDate))}
                                      </li>
                                      <li>
                                        Coupon Code:{" "}
                                        {user.appliedCoupon || "N/A"}
                                      </li>
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="no-plan">No Active Plan</span>
                            )}
                          </div>

                          <div className="plan-section">
                            <strong>History Plans:</strong>
                            {user.historyPlans &&
                            user.historyPlans.length > 0 ? (
                              <div className="plan-list">
                                {user.historyPlans.map((plan, index) => (
                                  <div key={index} className="plan-item">
                                    <p>
                                      <strong>
                                        {index + 1}. {plan.planName}:
                                      </strong>
                                    </p>
                                    <ul>
                                      <li>Price: ₹{plan.price}</li>
                                      <li>
                                        Start date:{" "}
                                        {new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.startDate))}
                                      </li>
                                      <li>
                                        Expired on:{" "}
                                        {new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.endDate))}
                                      </li>
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="no-plan">No History Plan</span>
                            )}
                          </div>

                          <p>
                            <strong>Total Calorie Search API Calls:</strong>{" "}
                            {user.totalCalorieSearchApiCalls}
                          </p>
                          <div className="plan-section">
                            <strong>Active Calorie Tracker Plans:</strong>
                            {user.activeCalorieTrackerBySearchPlan &&
                            user.activeCalorieTrackerBySearchPlan.length > 0 ? (
                              <div className="plan-list">
                                {user.activeCalorieTrackerBySearchPlan.map(
                                  (plan: any, index: number) => (
                                    <div key={index} className="plan-item">
                                      <p>
                                        <strong>
                                          {index + 1}. {plan.planName}:
                                        </strong>
                                      </p>
                                      <ul>
                                        <li>Price: ₹{plan.price}</li>
                                        <li>
                                          Start date:{" "}
                                          {new Intl.DateTimeFormat("en-IN", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                            timeZone: "Asia/Kolkata",
                                          }).format(new Date(plan.startDate))}
                                        </li>
                                        <li>
                                          End date:{" "}
                                          {new Intl.DateTimeFormat("en-IN", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                            timeZone: "Asia/Kolkata",
                                          }).format(new Date(plan.endDate))}
                                        </li>
                                      </ul>
                                    </div>
                                  )
                                )}
                              </div>
                            ) : (
                              <span className="no-plan">
                                No Active Calorie Tracker Plan
                              </span>
                            )}
                          </div>
                          <div className="plan-section">
                            <strong>History Calorie Tracker Plans:</strong>
                            {user.historyCalorieTrackerBySearchPlans &&
                            user.historyCalorieTrackerBySearchPlans.length >
                              0 ? (
                              <div className="plan-list">
                                {user.historyCalorieTrackerBySearchPlans.map(
                                  (plan: any, index: number) => (
                                    <div key={index} className="plan-item">
                                      <p>
                                        <strong>
                                          {index + 1}. Calorie Tracker Plan:
                                        </strong>
                                      </p>
                                      <ul>
                                        <li>Price: ₹{plan.price}</li>
                                        <li>
                                          Start date:{" "}
                                          {new Intl.DateTimeFormat("en-IN", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                            timeZone: "Asia/Kolkata",
                                          }).format(new Date(plan.startDate))}
                                        </li>
                                        <li>
                                          Expired on:{" "}
                                          {new Intl.DateTimeFormat("en-IN", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                            timeZone: "Asia/Kolkata",
                                          }).format(new Date(plan.endDate))}
                                        </li>
                                      </ul>
                                    </div>
                                  )
                                )}
                              </div>
                            ) : (
                              <span className="no-plan">
                                No History Calorie Tracker Plan
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="user-card-actions">
                        {user.dietPlan && user.dietPlan.length > 0 && (
                          <button
                            onClick={() => {
                              setCurrentDietPlan(user.dietPlan);
                              setShowDietPlanModal(true);
                              setSelectedMealType(
                                user.dietPlan[0]?.mealType ||
                                  "General Instructions"
                              ); // Set default selected meal type
                            }}
                            className="button button-view-diet"
                          >
                            View Diet Plan
                          </button>
                        )}
                        <button
                          onClick={() =>
                            openAssignSalesPersonModal(user.phoneNumber)
                          }
                          className="button button-status"
                        >
                          Status: {user.clientStatus || "N/A"}
                        </button>
                        <button
                          onClick={() =>
                            openAssignSalesPersonResponseModal(
                              user.phoneNumber,
                              user.comments
                            )
                          }
                          className="button button-comments"
                        >
                          Comments: {user.comments ? "View" : "Add"}
                        </button>
                        <span className="last-online">
                          Last Online: {timeAgo(user.lastOnline.toString())}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-users-found">
                  No clients found matching your criteria.
                </p>
              )}
            </div>
          </main>
        </>
      )}
    </div>
  );
};

export default ClientPanel;
